<template>
  <div style="margin-top: 16px">
    <Card style="margin-bottom: 24px">
      <Row>
        <i-col span="12">
          <h3 style="text-align: center">
            LDW Table Description Annotated
          </h3>
          <div style="height: 400px">
            <PieChart
              :data="tableDescriptionPieChartData"
              radius="75%"
            />
          </div>
        </i-col>
        <i-col span="12">
          <h3 style="text-align: center">
            LDW Table Column Description Annotated
          </h3>
          <div style="height: 400px">
            <PieChart
              :data="tableColDescriptionPieChartData"
              radius="75%"
              :show-download="false"
            />
          </div>
        </i-col>
      </Row>
    </Card>

    <FilterPanel
      v-model="filterPanelData.searchText"
      @onFilterResults="filterTableMetadata"
    >
      <template #top>
        <div class="filter-panel">
          <Row>
            Keywords Criteria
          </Row>
          <Row>
            <i-col span="12">
              <Select
                v-model="filterPanelData.filterCriteria"
                style="margin-right: 4px"
                @on-change="filterTableMetadata"
              >
                <Option
                  value="all"
                  label="All"
                />
                <Option
                  value="table"
                  label="Table Name"
                />
                <Option
                  value="field"
                  label="Field Name"
                />
              </Select>
            </i-col>
            <i-col span="12">
              <Select
                v-model="filterPanelData.filterMode"
                style="margin-left: 4px"
                @on-change="filterTableMetadata"
              >
                <Option value="abc">
                  abc
                </Option>
                <Option value="regex">
                  .*
                </Option>
              </Select>
            </i-col>
          </Row>
          <Row>
            <p>By Schema</p>
          </Row>
          <Row>
            <Select
              v-model="filterPanelData.selectedSchema"
              clearable
              placeholder="Select Schema"
              @on-change="filterTableMetadata"
            >
              <Option
                v-for="schema in schemaList"
                :key="schema"
                :value="schema"
                :label="schema"
              />
            </Select>
          </Row>
        </div>
      </template>
    </FilterPanel>
    <h1 style="margin-bottom: 16px; text-align: center">
      Tables Needing Annotations
    </h1>

    <div v-if="!ldwTablesForList.length">
      No Tables Found
    </div>
    <div v-else>
      <Card
        v-for="(table, index) in tablesForPage"
        :key="index"
        style="margin-bottom: 16px"
      >
        <h3 style="margin-bottom: 16px">
          {{ fullTableName(table) }}
        </h3>
        <div
          v-if="!table.description"
          style="margin-bottom: 16px"
        >
          <i class="fas fa-exclamation-triangle" /> Missing Table Description
        </div>
        <div v-if="table.columns.length">
          <div style="margin-bottom: 8px">
            <strong>Columns Missing Descriptions:</strong>
          </div>
          <List
            class="missing-col-list"
            border
          >
            <ListItem
              v-for="(column, colIndex) in table.columns"
              :key="colIndex"
              :class="colClassName(column)"
            >
              {{ column.name }}
            </ListItem>
          </List>
        </div>
      </Card>
      <Page
        :current="page + 1"
        :page-size="pageSize"
        :total="ldwTablesForList.length"
        show-elevator
        @on-change="onPageChange"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import _ from 'lodash'

import PieChart from './helper-components/chart-components/PieChart'
import FilterPanel from './helper-components/FilterPanel'

export default {
  components: {
    FilterPanel,
    PieChart
  },

  data () {
    return {
      tableMetadata: [],
      filteredTableMetadata: [],
      page: 0,
      pageSize: 5,
      filterPanelData: {
        searchText: '',
        selectedSchema: null,
        filterCriteria: 'all',
        filterMode: 'abc'
      }
    }
  },

  computed: {
    ldwTables () {
      return this.tableMetadata.filter(table => table.databaseName.toLowerCase() === 'ldw')
    },

    ldwTablesForList () {
      return (
        this.filteredTableMetadata
          .filter(table => (
            table.databaseName.toLowerCase() === 'ldw' &&
            (!table.description || table.columns.some(column => this.columnMissingDescription(column)))
          ))
          .map(table => {
            const formattedTable = { ...table }

            formattedTable.columns = table.columns.filter(column => this.columnMissingDescription(column))

            return formattedTable
          })
      )
    },

    tablesForPage () {
      const startingIndex = this.page * this.pageSize

      return this.ldwTablesForList.slice(startingIndex, startingIndex + this.pageSize)
    },

    ldwTablesWithoutDescription () {
      return this.ldwTables.filter(table => !table.description)
    },

    ldwTableColumnsWithoutDescription () {
      const columns = []

      this.ldwTables.forEach(table => {
        table.columns.forEach(column => {
          if (this.columnMissingDescription(column)) {
            columns.push(column)
          }
        })
      })

      return columns
    },

    totalNumColumns () {
      return this.ldwTables.reduce((count, table) => count + table.columns.length, 0)
    },

    tableDescriptionPieChartData () {
      const numNoDescription = this.ldwTablesWithoutDescription.length

      return {
        labels: [ 'No Table Description', 'Has Table Description' ],
        values: [ numNoDescription, this.ldwTables.length - numNoDescription ]
      }
    },

    tableColDescriptionPieChartData () {
      const numNoDescription = this.ldwTableColumnsWithoutDescription.length

      return {
        labels: [ 'No Column Description', 'Has Column Description' ],
        values: [ numNoDescription, this.totalNumColumns - numNoDescription ]
      }
    },

    schemaList () {
      const schemaSet = new Set()

      this.ldwTables.forEach(table => {
        const { schemaName } = table
        const schemaNameUpper = schemaName.toUpperCase()

        schemaSet.add(schemaNameUpper)
      })

      return Array.from(schemaSet)
    },

    ...mapState('snowflake', {
      snowflakeData: state => state.snowflakeData
    })
  },

  async mounted () {
    if (_.isEmpty(this.snowflakeData)) {
      await this.setSnowflakeData()
    }

    this.setTableMetadata()
  },

  methods: {
    /**
     * Refreshes the snowflake data in vuex
     * @return {Promise}
     */
    setSnowflakeData () {
      return new Promise(async (resolve, reject) => {
        try {
          await this.$store.dispatch('snowflake/fetchSnowflakeData')
          resolve()
        } catch (error) {
          console.error(`Error while setting snowflake data: ${error}`)
          reject(error)
        }
      })
    },

    /**
     * Fetches Table Metadata and sets it in the data object
     */
    async setTableMetadata () {
      try {
        this.tableMetadata = [...this.snowflakeData.tables]
        this.filteredTableMetadata = [...this.snowflakeData.tables]
      } catch (error) {
        const message = `Error while fetching table metadata: ${error}`

        this.$Message.error(message)
      }
    },

    /**
     * Determines if the column is missing a description
     * @param {column} - Column object
     * @return {Boolean} - True if missing description
     */
    columnMissingDescription (column) {
      return !column.description && column.name.toLowerCase() !== 'total_duration_in_mins'
    },

    /**
     * Event handler for when the page changes
     * @param {String} pageNumber - The new page number
     */
    onPageChange (pageNumber) {
      this.page = pageNumber - 1
    },

    /**
     * Returns the full table name of a table: database.schema.table
     * @param {Object} table - The table object
     * @return {String} - The full table name
     */
    fullTableName (table) {
      const { databaseName, schemaName, name } = table

      return `${databaseName}.${schemaName}.${name}`
    },

    /**
     * Resets the filter panel's settings
     */
    resetFilterPanelData () {
      this.$set(this.filterPanelData, 'searchText', '')
      this.$set(this.filterPanelData, 'filterCriteria', 'all')
      this.$set(this.filterPanelData, 'filterMode', 'abc')
      this.$set(this.filterPanelData, 'selectedSchema', null)
    },

    /**
     * Filters the table metadata
     * @param {Object} filterParams - The paramters set for the filter panel
     */
    filterTableMetadata (filterParams) {
      if (filterParams && filterParams.resetFilters) {
        this.resetFilterPanelData()
      }

      this.filteredTableMetadata = this.tableMetadata.filter(
        table => this.searchTextMatch(table) && this.schemaMatch(table)
      )
    },

    /**
     * Determines whether or not the table is matched through the search text
     * Based on multiple criterias
     * @param {Object} table - The table object
     * @return {Boolean} - True if the table fulfills search texts requirements
     */
    searchTextMatch (table) {
      const { schemaName, name } = table
      const { filterCriteria } = this.filterPanelData

      if (filterCriteria === 'all') {
        return this.nameMatch(this.fullTableName(table)) || this.tableHasColMatch(table)
      } else if (filterCriteria === 'schema') {
        return this.nameMatch(schemaName)
      } else if (filterCriteria === 'table') {
        return this.nameMatch(name)
      } else if (filterCriteria === 'field') {
        return this.tableHasColMatch(table)
      }

      return false
    },

    /**
     * Determines whether or not the name can return a match given the search text
     * @param {String} name - The name we are checking a match for
     * @return {Boolean} - True if the name returns a match
     */
    nameMatch (name) {
      const { filterMode, searchText } = this.filterPanelData
      const regexStr = filterMode === 'regex' ? searchText : this.escapeRegex(searchText)
      const regex = new RegExp(regexStr, 'i')

      return name.match(regex)
    },

    /**
     * Determines whether or not the table has a column whose name returns a match
     * @param {Object} table - The table object
     * @return {Boolean} - True if table has at least one column that matches the search text
     */
    tableHasColMatch (table) {
      return table.columns.some(column => this.nameMatch(column.name))
    },

    /**
     * Determines whether or not the table is within the selected schema within the filter panel
     * @param {Object} table - The table object
     * @return {Boolean} - True if the schema matches
     */
    schemaMatch (table) {
      const { selectedSchema } = this.filterPanelData
      return !selectedSchema || table.schemaName.toLowerCase() === selectedSchema.toLowerCase()
    },

    /**
     * Escapes the given string from any special regex characters
     * @param {String} str - String that we are replacing regex characters from
     * @return {String} - The escaped regex string
     */
    escapeRegex (str) {
      return str.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
    },

    /**
     * Determines the css class for a given table column
     * @param {Object} col - The table column object
     * @return {String} - The class name string
     */
    colClassName (col) {
      const { filterCriteria, searchText } = this.filterPanelData
      if ((filterCriteria === 'all' || filterCriteria === 'field') && this.nameMatch(col.name) && searchText) {
        return 'highlighted-list-item'
      }

      return ''
    }
  }
}
</script>
<style scoped>
  .filter-panel >>> .ivu-row {
    margin-bottom: 4px;
  }

  .missing-col-list >>> .highlighted-list-item {
    background-color: var(--color-blue);
    color: var(--color-white);
    font-weight: bold;
  }
</style>
